/**
 * Modal para o admin parear uma TV com o painel financeiro.
 * O admin insere o código de 6 caracteres exibido na TV.
 */
import { useRef, useState } from 'react';
import { PiTelevisionSimpleBold, PiCheckCircleBold, PiXBold } from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

type Props = {
    open: boolean;
    onClose: () => void;
};

type Phase = 'input' | 'submitting' | 'success' | 'error';

export default function ApproveTvModal({ open, onClose }: Props) {
    const [code, setCode]       = useState('');
    const [phase, setPhase]     = useState<Phase>('input');
    const [errorMsg, setErrorMsg] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    if (!open) return null;

    const handleApprove = async () => {
        const clean = code.trim().toUpperCase();
        if (clean.length !== 6) {
            setErrorMsg('O código deve ter exatamente 6 caracteres.');
            return;
        }
        setPhase('submitting');
        setErrorMsg('');
        try {
            await api.post('/tv/pair/approve', { code: clean }, { baseURL: '/api' });
            setPhase('success');
        } catch (err: any) {
            setPhase('error');
            setErrorMsg(err?.response?.data?.message ?? 'Erro ao autorizar TV.');
        }
    };

    const handleClose = () => {
        setCode('');
        setPhase('input');
        setErrorMsg('');
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-zinc-900">
                {/* Fechar */}
                <button
                    type="button"
                    onClick={handleClose}
                    className="absolute right-4 top-4 rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10"
                >
                    <PiXBold className="size-4" />
                </button>

                {/* Cabeçalho */}
                <div className="flex items-center gap-3">
                    <span className="flex size-10 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/40">
                        <PiTelevisionSimpleBold className="size-5 text-indigo-600 dark:text-indigo-400" />
                    </span>
                    <div>
                        <h2 className="font-bold text-gray-900 dark:text-white">Parear TV</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Painel Financeiro · Modo TV</p>
                    </div>
                </div>

                {phase === 'success' ? (
                    <div className="mt-8 text-center">
                        <PiCheckCircleBold className="mx-auto size-14 text-emerald-500" />
                        <p className="mt-3 text-lg font-bold text-gray-900 dark:text-white">TV autorizada!</p>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            O painel será exibido em fullscreen automaticamente.
                        </p>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="mt-6 w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
                        >
                            Fechar
                        </button>
                    </div>
                ) : (
                    <>
                        <p className="mt-5 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                            Na TV, abra o endereço abaixo no navegador. Em seguida, digite o código de 6 caracteres exibido na tela.
                        </p>

                        <div className="mt-3 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-white/10 dark:bg-zinc-800/80">
                            <code className="min-w-0 flex-1 truncate font-mono text-xs font-semibold text-gray-800 dark:text-gray-200">
                                {`${window.location.origin}/tv/finance`}
                            </code>
                            <button
                                type="button"
                                onClick={() => {
                                    void navigator.clipboard.writeText(`${window.location.origin}/tv/finance`);
                                }}
                                className="shrink-0 rounded-lg px-2 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950/40"
                            >
                                Copiar
                            </button>
                        </div>

                        {/* Input de código */}
                        <div className="mt-4">
                            <input
                                ref={inputRef}
                                type="text"
                                value={code}
                                onChange={(e) => { setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')); setErrorMsg(''); }}
                                maxLength={6}
                                placeholder="ABC123"
                                className={cn(
                                    'w-full rounded-xl border px-4 py-3 text-center font-mono text-3xl font-black uppercase tracking-[0.2em] outline-none transition',
                                    'border-gray-300 bg-white text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200',
                                    'dark:border-white/10 dark:bg-zinc-800 dark:text-white dark:focus:border-indigo-500 dark:focus:ring-indigo-900/40',
                                    errorMsg && 'border-red-500 focus:border-red-500',
                                )}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApprove(); }}
                                autoFocus
                            />
                            {errorMsg && <p className="mt-1.5 text-xs text-red-500">{errorMsg}</p>}
                        </div>

                        <button
                            type="button"
                            onClick={handleApprove}
                            disabled={phase === 'submitting' || code.length !== 6}
                            className="mt-4 w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {phase === 'submitting' ? 'Autorizando…' : 'Autorizar TV'}
                        </button>

                        {phase === 'error' && !errorMsg && (
                            <p className="mt-2 text-xs text-red-500">Erro ao autorizar. Tente novamente.</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
